import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ManualPromise } from "abweb/utils/ManualPromise";

/**
 * Static class which wraps some functionality to allow throttle to throttle the number of rpcs in flight at once.
 * Usage:
 * Before making an throttle request one should call isQuerySlotOpen. If it returns true then make the call. Otherwise,
 * if it returns a promise, wait for that promise to resolve before making an throttle call
 * **with the most recent query string, not the string at the time that the promise was returned**
 *
 * Making the call:
 * After making the rpc, call registerRPCPromise and pass the rpc promise to the class.
 * Usage Note - when you wait on the promise, you need to check if query slot is open again before sending your RPC.
 * Assume that the resolution of the promise tells you that there **may** be a slot open.
 *
 */

export class AbQueryThrottleImpl {
  constructor() {
    _defineProperty(this, "inFlightQueryCount", 0);
    _defineProperty(this, "mostRecentQueryInFlight", undefined);
    _defineProperty(this, "mostRecentQueryID", 0);
  }
  fireQueryIfSlotOpenOrWaitForSlot(fireQueryFunction, handleQueryReturn, handleQueryError) {
    const localId = ++this.mostRecentQueryID;
    this.fireQueryIfSlotOpenOrWaitForSlotRecursive(fireQueryFunction, handleQueryReturn, localId, handleQueryError);
  }
  fireQueryIfSlotOpenOrWaitForSlotRecursive(fireQueryFunction, handleQueryReturn, localRPCID, handleQueryError) {
    const querySlotOpen = this.isQuerySlotOpen();
    if (this.mostRecentQueryID !== localRPCID) {
      return;
    }
    if (querySlotOpen === true) {
      let promise = fireQueryFunction().then(items => {
        if (localRPCID !== this.mostRecentQueryID) {
          return;
        }
        handleQueryReturn(items);
      }).catch(error => {
        if (handleQueryError) {
          handleQueryError(error);
        } else {
          throw error;
        }
      });
      this.registerRPCPromise(promise);
    }
    // if the query throttler does not return true, then it will return a promise which represents the blocking query
    else {
      querySlotOpen.finally(() => {
        this.fireQueryIfSlotOpenOrWaitForSlotRecursive(fireQueryFunction, handleQueryReturn, localRPCID, handleQueryError);
      });
    }
  }

  /**
   * Method which should be called before making an throttle rpc.
   * If there is a query slot open, will return true and throttle is free to make rpc.
   * If there is no query slot open, will return a promise which represents the last query in flight which throttle
   * should wait to complete before sending new rpc.
   */
  isQuerySlotOpen() {
    if (this.inFlightQueryCount < 2) {
      return true;
    } else if (this.inFlightQueryCount === 2) {
      if (this.mostRecentQueryInFlight === undefined) {
        throw new Error("Must keep track of most recent query in flight if query count is 2");
      }
      return this.mostRecentQueryInFlight;
    } else {
      throw new Error("in flight query count should never be greater than 2");
    }
  }

  /**
   * method which throttle should call when making an rpc passing the rpc promise.
   * @param rpcPromise - promise which represents the throttling request
   */
  registerRPCPromise(rpcPromise) {
    this.inFlightQueryCount = this.inFlightQueryCount + 1;
    // Wrapping the registered promise with a manual promise to ensure that the promise we return never rejects.
    let manualPromise = new ManualPromise();
    this.mostRecentQueryInFlight = manualPromise.promise;
    rpcPromise.finally(() => {
      if (this.inFlightQueryCount === 0) {
        throw new Error("Query count should never go below 0");
      }
      this.inFlightQueryCount = this.inFlightQueryCount - 1;
      // if and only if this promise is the most recent query in flight - clear the reference
      if (this.mostRecentQueryInFlight === manualPromise.promise) {
        this.mostRecentQueryInFlight = undefined;
      }
      manualPromise.resolve();
    });
  }
}
//# sourceMappingURL=AbQueryThrottle.js.map
